import { useState } from "react";
import {
	Box,
	Button,
	List,
	ListItemButton,
	ListItemText,
	Typography,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { pl, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { filterDatePeriods } from "./filterDatePeriods";

const isSameDay = (a, b) =>
	a?.getDate() === b?.getDate() &&
	a?.getMonth() === b?.getMonth() &&
	a?.getFullYear() === b?.getFullYear();

const isInRange = (date, start, end) =>
	date && start && end && date > start && date < end;

const listItemStyles = (theme) => ({
	"&.Mui-selected": {
		backgroundColor: theme.palette.active.main,
		color: theme.palette.primary.contrastText,
	},
	"&.Mui-selected:hover": {
		backgroundColor: theme.palette.active.main,
	},
	borderRadius: "10px",
});

const calendarStyles = {
	height: "100%",
	"& .MuiPickersCalendarHeader-root": { marginTop: 0, marginBottom: 0 },
	"& .MuiDayCalendar-header": { paddingLeft: 0, paddingRight: 0 },
	"& .MuiDayCalendar-weekContainer": { margin: 0 },
	"& .MuiPickersSlideTransition-root": { minHeight: "190px" },
	mx: 0,
};

const CustomDay = ({ day, startDate, endDate, theme, ...other }) => {
	const start = isSameDay(day, startDate);
	const end = isSameDay(day, endDate);
	const between = isInRange(day, startDate, endDate);

	const parentBg =
		start || end
			? theme.palette.active.main
			: between
				? theme.palette.background.default
				: "transparent";

	const borderRadius = start
		? "50% 0 0 50%"
		: end
			? "0 50% 50% 0"
			: between
				? 0
				: "50%";

	return (
		<Box
			sx={{
				backgroundColor: parentBg,
				borderRadius,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				transition: "background-color 0.2s",
			}}
		>
			<PickersDay
				{...other}
				day={day}
				selected={false}
				sx={{
					backgroundColor: "transparent !important",
					color:
						start || end || between
							? theme.palette.text.primary
							: theme.palette.text.secondary,
					fontWeight: start || end ? "bold" : "normal",
					"&.Mui-focusVisible": { backgroundColor: "transparent" },
				}}
			/>
		</Box>
	);
};
const PredefinedRangesList = ({ startDate, endDate, onSelect, theme }) => {
	const { t } = useTranslation();
	return (
		<List dense>
			{filterDatePeriods.map((range, index) => (
				<ListItemButton
					key={index}
					onClick={() => onSelect(range)}
					selected={
						startDate?.getTime() === range.startDate.getTime() &&
						endDate?.getTime() === range.endDate.getTime()
					}
					sx={listItemStyles(theme)}
				>
					<ListItemText primary={t(range.label)} />
				</ListItemButton>
			))}
		</List>
	);
};

export function CustomDateRangePicker({
	initialStartDate,
	initialEndDate,
	onApply,
}) {
	const [startDate, setStartDate] = useState(initialStartDate);
	const [endDate, setEndDate] = useState(initialEndDate);
	const [selectingStart, setSelectingStart] = useState(true);

	const userLanguage = localStorage.getItem("i18nextLng");
	const localesMap = { pl, en: enUS };
	const dateLocale = localesMap[userLanguage] || enUS;
	const { t } = useTranslation();
	const theme = useTheme();

	const handleDateChange = (newDate) => {
		if (selectingStart) {
			setStartDate(newDate);
			setEndDate(null);
		} else if (newDate >= startDate) {
			setEndDate(newDate);
		} else {
			setStartDate(newDate);
			setEndDate(null);
		}
		setSelectingStart(!selectingStart);
	};

	const handlePredefinedRange = (range) => {
		setStartDate(range.startDate);
		setEndDate(range.endDate);
		setSelectingStart(true);
	};

	const handleApply = () => {
		if (startDate && endDate) onApply({ startDate, endDate });
	};

	return (
		<LocalizationProvider
			dateAdapter={AdapterDateFns}
			adapterLocale={dateLocale}
		>
			<Box
				sx={{
					display: "flex",
					borderRadius: "10px",
					overflow: "hidden",
				}}
			>
				{filterDatePeriods.length > 0 && (
					<Box sx={{ borderRight: 1, borderColor: "divider", p: 1 }}>
						<PredefinedRangesList
							startDate={startDate}
							endDate={endDate}
							onSelect={handlePredefinedRange}
							theme={theme}
						/>
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<Box
						sx={{
							p: 2,
							borderBottom: 1,
							borderColor: "divider",
							display: "flex",
							alignItems: "center",
							gap: 2,
						}}
					>
						<Typography fontWeight={500}>
							{startDate
								? startDate.toLocaleDateString(dateLocale)
								: "---"}
						</Typography>
						<ArrowRightAltIcon fontSize="small" color="action" />
						<Typography fontWeight={500}>
							{endDate
								? endDate.toLocaleDateString(dateLocale)
								: "---"}
						</Typography>
						<Button
							variant="outlined"
							onClick={handleApply}
							disabled={!startDate || !endDate}
						>
							{t("apply")}
						</Button>
					</Box>
					<DateCalendar
						value={selectingStart ? startDate : endDate}
						onChange={handleDateChange}
						sx={calendarStyles}
						minDate={new Date("2000-01-01")}
						maxDate={new Date()}
						slots={{
							day: (props) => (
								<CustomDay
									{...props}
									startDate={startDate}
									endDate={endDate}
									theme={theme}
								/>
							),
						}}
					/>
				</Box>
			</Box>
		</LocalizationProvider>
	);
}
